import { Avatar, IconButton, Tooltip } from "@mui/material";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { useAppDispatch, useAppSelector } from "../../Store";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { allMessagesStore } from "../../Store/Slices/socket/AllMessagesSlice";
import {
  botInfoStore,
  setOpenStatus,
  setUserExpanded,
} from "../../Store/Slices/socket/BotInfoSlice";
import { socketStatusStore } from "../../Store/Slices/socket/SocketSlice";
import {
  StyledWidgetHeader,
  WidgetHeaderAvatarWrap,
  WidgetHeaderBrand,
  WidgetHeaderBrandTitle,
  WidgetHeaderRight,
  WidgetHeaderStatus,
  WidgetHeaderStatusDot,
} from "../Styles/StyledScreens";
import {
  AppLanguageSelector,
  AppRenderNotificationMenu
} from "../appComponents/Features";
import AppRenderQuickMenuHeader from "../appComponents/Features/AppRenderQuickMenuHeader";
import { AppMaterialIcons } from "../appComponents/Icons";
import AppEndChatFeedback from "../appComponents/UtilsComponents/AppEndChatFeedback";
import AppRestartChat from "../appComponents/UtilsComponents/AppRestartChat";
import { PreviewType } from "../utils/Enum";
import { getHeaderStatusText, sendDataToParent } from "../utils/utils";

interface Props {
  setShowStartScreen?: (value: boolean) => void;
  onFormSubmitted?: () => void;
}

const Header: React.FC<Props> = ({ setShowStartScreen, onFormSubmitted }) => {
  const theme:any=useTheme();
  const dispatch = useAppDispatch();
  const { widgetPreviewType, isBotSetToFullScreen, isUserExpanded } = useSelector(botInfoStore);
  const { botConfig } = useSelector(useBotConfigStore);
  const { typingStatus } = useAppSelector(allMessagesStore);
  const { connectionStatus } = useAppSelector(socketStatusStore);

  const headerStatusText = useMemo(
    () =>
      getHeaderStatusText(
        connectionStatus !== "disconnected",
        typingStatus,
        theme?.industry_style?.enabled ?? false
      ),
    [connectionStatus, typingStatus, theme]
  );

  const handleCloseChat = () => {
    const alwaysOpen = botConfig?.config?.always_open ?? false;
    !alwaysOpen && dispatch(setOpenStatus(false));
    
    const enableOnChatbotCloseEvent = botConfig?.config?.enable_on_chatbot_close_event ?? false;
    enableOnChatbotCloseEvent && sendDataToParent("onChatbotClose", "close");
  };
  return (
    <StyledWidgetHeader>
      <AppRenderQuickMenuHeader />
      <WidgetHeaderBrand>
        <WidgetHeaderAvatarWrap>
          <Avatar
            src={botConfig.config?.company_logo?.source || undefined}
            alt={botConfig?.chatbot?.name}
          >
            {botConfig?.chatbot?.name?.trim()?.charAt(0)?.toUpperCase()}
          </Avatar>
          {theme?.industry_style?.enabled ? (
            <WidgetHeaderStatusDot
              $online={connectionStatus !== "disconnected"}
            />
          ) : null}
        </WidgetHeaderAvatarWrap>
        <div className="titleBox">
          <WidgetHeaderBrandTitle title={botConfig?.chatbot?.name}>
            {botConfig?.chatbot?.name}
          </WidgetHeaderBrandTitle>
          <WidgetHeaderStatus>{headerStatusText}</WidgetHeaderStatus>
        </div>
      </WidgetHeaderBrand>
      <WidgetHeaderRight>
        <AppLanguageSelector setShowStartScreen={setShowStartScreen} onFormSubmitted={onFormSubmitted} />
        <AppRenderNotificationMenu />
        {isBotSetToFullScreen || !theme?.industry_style?.enabled ? null : (
          <Tooltip title={isUserExpanded ? "Collapse" : "Expand"}>
            <IconButton
              aria-label={
                isUserExpanded ? "Collapse chat window" : "Expand chat window"
              }
              onClick={() => dispatch(setUserExpanded(!isUserExpanded))}
              disabled={
                widgetPreviewType === PreviewType.getaWidgetPreview ? true : false
              }
            >
              <AppMaterialIcons
                iconName={isUserExpanded ? "FullscreenExit" : "Fullscreen"}
                className="headerIconMd"
              />
            </IconButton>
          </Tooltip>
        )}
        {isBotSetToFullScreen ? null : (
          <IconButton
            aria-label="minimize chat"
            onClick={() => {
              handleCloseChat();
            }}
            disabled={
              widgetPreviewType === PreviewType.getaWidgetPreview ? true : false
            }
          >
            <AppMaterialIcons
              iconName="ExpandMore"
              className="headerIconLg"
            />
          </IconButton>
        )}
        {widgetPreviewType === PreviewType.getaWidgetPreview ||
        isBotSetToFullScreen ? null : (
            <AppEndChatFeedback />
        )}
        {isBotSetToFullScreen ? <AppRestartChat /> : null}
      </WidgetHeaderRight>
  </StyledWidgetHeader>
  );
};

export default Header;
